import axios, { AxiosInstance, AxiosError } from "axios";

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || "http://localhost:3001/api",
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Добавляем токен к каждому запросу
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Обработка ошибок
    this.api.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(username: string, password: string) {
    const response = await this.api.post("/auth/login", {
      login: username,
      password,
    });
    return response.data;
  }

  async register(data: any) {
    const response = await this.api.post("/auth/register", data);
    return response.data;
  }

  async getProfile() {
    const response = await this.api.get("/auth/profile");
    return response.data;
  }

  // Users
  async getUsers() {
    const response = await this.api.get("/users");
    return response.data;
  }

  async createUser(data: any) {
    const response = await this.api.post("/users", data);
    return response.data;
  }

  // Merchants
  async getMerchants() {
    const response = await this.api.get("/merchants");
    return response.data.data || response.data || [];
  }

  async getMerchant(id: string) {
    const response = await this.api.get(`/merchants/${id}`);
    return response.data;
  }

  // Providers
  async getProviders() {
    const response = await this.api.get("/providers");
    return response.data.data || response.data || [];
  }

  async getProvider(id: string) {
    const response = await this.api.get(`/providers/${id}`);
    return response.data;
  }

  // Orders
  async getOrders(params?: any) {
    const response = await this.api.get("/orders", { params });
    return response.data;
  }

  async getOrder(id: string) {
    const response = await this.api.get(`/orders/${id}`);
    return response.data;
  }

  async createOrder(data: any) {
    const response = await this.api.post("/orders", data);
    return response.data;
  }

  async updateOrderStatus(id: string, status: string) {
    const response = await this.api.patch(`/orders/${id}/status`, { status });
    return response.data;
  }

  // Merchant API для создания ордера
  async createMerchantOrder(apiKey: string, data: any) {
    const response = await this.api.post("/orders/create", data, {
      headers: {
        "X-API-Key": apiKey,
      },
    });
    return response.data;
  }

  // Appeals
  async getAppeals(params?: any) {
    const response = await this.api.get("/appeals", { params });
    return response.data;
  }

  async getAppeal(id: string) {
    const response = await this.api.get(`/appeals/${id}`);
    return response.data;
  }

  async createAppeal(data: any) {
    const response = await this.api.post("/appeals", data);
    return response.data;
  }

  async updateAppealStatus(id: string, status: string, comment?: string) {
    const response = await this.api.patch(`/appeals/${id}/status`, {
      status,
      comment,
    });
    return response.data;
  }

  // File upload
  async uploadFile(file: File) {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.api.post("/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  // Upload multiple files for appeals
  async uploadAppealFiles(files: File[]) {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const response = await this.api.post("/appeals/upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  }

  // Exchange rates
  async getExchangeRates() {
    const response = await this.api.get("/exchange-rates");
    return response.data;
  }

  // Banks
  async getActiveBanks() {
    const response = await this.api.get("/banks/active");
    return response.data;
  }

  // Payment methods (только активные, для dropdown'ов)
  async getActivePaymentMethods() {
    const response = await this.api.get("/traders/payment-methods/available");
    return response.data;
  }

  // Provider integration
  async createProviderOrder(data: any) {
    const response = await this.api.post("/integration/orders", data);
    return response.data;
  }

  async getProviderIntegrationBalance() {
    const response = await this.api.get("/integration/balance");
    return response.data;
  }

  async createProviderAppeal(data: any) {
    const response = await this.api.post("/integration/appeals", data);
    return response.data;
  }

  // Test panel specific
  async testProviderDeposit(data: {
    providerId: string;
    amount: number;
    balanceType?: "working" | "transit";
    comment?: string;
  }) {
    const response = await this.api.post(
      "/balance/test/provider-deposit",
      data
    );
    return response.data;
  }

  // Balance Testing Scenarios
  async getTestTraders() {
    const response = await this.api.get("/test/balance/traders");
    return response.data;
  }

  async getTraderBalances(traderId: string) {
    const response = await this.api.get(`/test/balance/trader/${traderId}`);
    return response.data;
  }

  async runScenarioFreeze(traderId: string, amount: number) {
    const response = await this.api.post("/test/balance/scenario/freeze", {
      traderId,
      amount,
    });
    return response.data;
  }

  async runScenarioUnfreezeSuccess(traderId: string, amount: number, income: number) {
    const response = await this.api.post("/test/balance/scenario/unfreeze-success", {
      traderId,
      amount,
      income,
    });
    return response.data;
  }

  async runScenarioUnfreezeCancel(traderId: string, amount: number) {
    const response = await this.api.post("/test/balance/scenario/unfreeze-cancel", {
      traderId,
      amount,
    });
    return response.data;
  }

  async runScenarioCreditIncome(traderId: string, amount: number) {
    const response = await this.api.post("/test/balance/scenario/credit-income", {
      traderId,
      amount,
    });
    return response.data;
  }

  async runScenarioHoldAppeal(traderId: string, amount: number) {
    const response = await this.api.post("/test/balance/scenario/hold-appeal", {
      traderId,
      amount,
      exchangeRate: 1,
    });
    return response.data;
  }

  async runScenarioReleaseAppeal(traderId: string, amount: number) {
    const response = await this.api.post("/test/balance/scenario/release-appeal", {
      traderId,
      amount,
      exchangeRate: 1,
    });
    return response.data;
  }

  async runFullOrderScenario(
    traderId: string,
    orderAmount: number,
    merchantFeePercentage: number,
    traderFeePercentage: number
  ) {
    const response = await this.api.post("/test/balance/scenario/full-order", {
      traderId,
      orderAmount,
      merchantFeePercentage,
      traderFeePercentage,
    });
    return response.data;
  }

  async runAppealFlowScenario(
    traderId: string,
    orderAmount: number,
    resolution: "APPROVED" | "REJECTED"
  ) {
    const response = await this.api.post("/test/balance/scenario/appeal-flow", {
      traderId,
      orderAmount,
      resolution,
    });
    return response.data;
  }

  async getBalanceLogs(traderId: string) {
    const response = await this.api.get(`/test/balance/logs/${traderId}`);
    return response.data;
  }

  async getTestMerchants() {
    const response = await this.api.get("/test/balance/merchants");
    return response.data;
  }

  async runFullSystemTest(
    traderId: string,
    merchantId: string,
    orderAmount: number,
    merchantFeePercentage: number,
    traderFeePercentage: number
  ) {
    const response = await this.api.post("/test/balance/scenario/full-system-test", {
      traderId,
      merchantId,
      orderAmount,
      merchantFeePercentage,
      traderFeePercentage,
    });
    return response.data;
  }

  async resetBalances(
    traderId: string,
    balances: {
      balance?: number;
      frozenBalance?: number;
      transitBalance?: number;
      disputeBalance?: number;
    }
  ) {
    const response = await this.api.post("/test/balance/reset-balances", {
      traderId,
      ...balances,
    });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;


